/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class FileSelectorInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Generates the HTML ID for a select input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the select input element.
	 */
	getControlContainerHTMLID(pInputHTMLID)
	{
		return `#CONTROL-FOR-${pInputHTMLID}`;
	}

	getInputHTMLID(pInputHTMLID)
	{
		return `#${pInputHTMLID}`;
	}

	getDisplayInputHTMLID(pInputHTMLID)
	{
		return `#DISPLAY-FOR-${pInputHTMLID}`;
	}

	getFileInputHTMLID(pInputHTMLID)
	{
		return `#FILE-INPUT-FOR-${pInputHTMLID}`;
	}

	getLoadingInputHTMLID(pInputHTMLID)
	{
		return `#LOADING-FOR-${pInputHTMLID}`;
	}

	getTabularControlContainerHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-CONTROL-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-DATA-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularDisplayInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-DISPLAY-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularFileInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-FILE-INPUT-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularLoadingInputHTMLID(pInputHTMLID, pRowIndex)
	{
		return `#TABULAR-LOADING-FOR-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * Hides the loading indicator for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	hideLoadingIndicator(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpLoadingElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularLoadingInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getLoadingInputHTMLID(pInputHTMLID));
		if (Array.isArray(tmpLoadingElements) && tmpLoadingElements.length > 0)
		{
			const tmpLoadingElement = tmpLoadingElements[0];
			if (tmpLoadingElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.addClass(tmpLoadingElement, 'is-hidden');
			}
		}
	}

	/**
	 * Shows the loading indicator for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	showLoadingIndicator(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpLoadingElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularLoadingInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getLoadingInputHTMLID(pInputHTMLID));
		if (Array.isArray(tmpLoadingElements) && tmpLoadingElements.length > 0)
		{
			const tmpLoadingElement = tmpLoadingElements[0];
			if (tmpLoadingElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.removeClass(tmpLoadingElement, 'is-hidden');
			}
		}
	}

	/**
	 * Hides the entry container for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	hideEntryContainer(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpControlContainerElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularControlContainerHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getControlContainerHTMLID(pInputHTMLID));
		if (Array.isArray(tmpControlContainerElements) && tmpControlContainerElements.length > 0)
		{
			const tmpControlContainerElement = tmpControlContainerElements[0];
			if (tmpControlContainerElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.addClass(tmpControlContainerElement, 'is-hidden');
			}
		}
	}

	/**
	 * Shows the entry container for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	showEntryContainer(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpControlContainerElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularControlContainerHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getControlContainerHTMLID(pInputHTMLID));
		if (Array.isArray(tmpControlContainerElements) && tmpControlContainerElements.length > 0)
		{
			const tmpControlContainerElement = tmpControlContainerElements[0];
			if (tmpControlContainerElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.removeClass(tmpControlContainerElement, 'is-hidden');
			}
		}
	}

	/**
	 * Hides the display container for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	hideDisplayContainer(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpDisplayContainerElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularDisplayInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getDisplayInputHTMLID(pInputHTMLID));
		if (Array.isArray(tmpDisplayContainerElements) && tmpDisplayContainerElements.length > 0)
		{
			const tmpDisplayContainerElement = tmpDisplayContainerElements[0];
			if (tmpDisplayContainerElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.addClass(tmpDisplayContainerElement, 'is-hidden');
			}
		}
	}

	/**
	 * Shows the display container for the given input HTML ID.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	showDisplayContainer(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpDisplayContainerElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularDisplayInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getDisplayInputHTMLID(pInputHTMLID));
		if (Array.isArray(tmpDisplayContainerElements) && tmpDisplayContainerElements.length > 0)
		{
			const tmpDisplayContainerElement = tmpDisplayContainerElements[0];
			if (tmpDisplayContainerElement instanceof HTMLElement)
			{
				this.pict.ContentAssignment.removeClass(tmpDisplayContainerElement, 'is-hidden');
			}
		}
	}

	/**
	 * This function updates the display container for a given input HTML ID with the provided value if the value corresponds to a valid observation.
	 * @param {*} pInputHTMLID - the HTML ID of the input element
	 * @param {*} pValue - the value to set
	 * @param {*} [pResult] - the observation result object (if already known)
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	async updateDisplayContainer(pInputHTMLID, pValue, pResult, isTabular = false, pRowIndex = null)
	{
		const tmpDisplayElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularDisplayInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getDisplayInputHTMLID(pInputHTMLID));
		let tmpObservation = pResult;
		// if no value is passed, clear it out and we're done
		if (!pValue)
		{
			// find the display element and update it
			if (Array.isArray(tmpDisplayElements) && tmpDisplayElements.length > 0)
			{
				const tmpDisplayElement = tmpDisplayElements[0];
				if (tmpDisplayElement instanceof HTMLElement)
				{
					this.pict.ContentAssignment.assignContent(tmpDisplayElement, '');
					return;
				}
			}
		}
		// look up the observation to get the Name and Description if we don't already have record available
		if (!tmpObservation)
		{
			tmpObservation = await this.pict.providers.ObservationProvider.getObservationByID(pValue);
		}
		// now build the template for display
		if (tmpObservation)
		{
			const tmpName = tmpObservation.Name || `Unnamed Observation #${pValue}`;
			const tmpFileType = this.pict.providers.ObservationProvider.getObservationFileType(tmpObservation);
			const tmpFileIcon = this.pict.providers.ObservationProvider.returnFileIconFromFileType(tmpFileType);
			const tmpProjectID = this.pict.AppData?.DocumentData?.ReportData?.IDProject;
			// set the input value to the ID and Name in a predefined format
			let tmpTemplate = '';
			// if we're in interactive mode, add links for rename and delete
			if (this.pict.AppData.Interactive && !this.pict.AppData?.DocumentData?.ReportData?.Locked)
			{
				tmpTemplate = /*HTML*/`
					<span class="tag has-background-text-90 mr-2">
						<span class="pr-1">
							<i class="fa fa-fw ${tmpFileIcon}"></i>
						</span>
						<span class="icon-text">${tmpFileType}</span>
					</span>
					<a class="has-text-text" href="javascript:void(0);" onclick="window._Pict.views.HeadlightPaneObservations.showObservationDetailView(event, ${pValue});">
						<strong class="mr-1">${tmpName}</strong>
					</a>
					<a class="ml-1 has-text-link" href="javascript:void(0);" onclick="window._Pict.providers.FileSelector.handleRenameFile('${pInputHTMLID}', ${pValue}, '${tmpName}', ${isTabular}, ${pRowIndex});" title="Rename File">
						<i class="fa fa-fw fa-edit"></i>
					</a>
					<a class="ml-1 has-text-danger" href="javascript:void(0);" onclick="window._Pict.providers.FileSelector.handleRemoveFile('${pInputHTMLID}', ${isTabular}, ${pRowIndex});" title="Remove File">
						<i class="fa fa-fw fa-times-circle"></i>
					</a>`;
			}
			else
			{
				tmpTemplate = /*HTML*/`
					<span class="tag has-background-text-90 mr-2">
						<span class="pr-1">
							<i class="fa fa-fw ${tmpFileIcon}"></i>
						</span>
						<span class="icon-text">${tmpFileType}</span>
					</span>
					<a href="${ this.pict.providers.UtilityProvider.buildLinksUrl() }/#/project/${ tmpProjectID }/journal/list/observation/${ pValue }" target="_blank" rel="noopener noreferrer">
						<strong class="mr-1">${tmpName}</strong>
					</a>`;
			}
			// find the display element and update it
			if (Array.isArray(tmpDisplayElements) && tmpDisplayElements.length > 0)
			{
				const tmpDisplayElement = tmpDisplayElements[0];
				if (tmpDisplayElement instanceof HTMLElement)
				{
					this.pict.ContentAssignment.assignContent(tmpDisplayElement, tmpTemplate);
				}
			}
		}
	}

	/**
	 * This function opens the observation search panel to allow the user to select an observation to associate with the input.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {*} pViewHash - the hash of the current view
	 * @param {*} pInputHash - the hash of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	openObservationSearchPanel(pInputHTMLID, pViewHash, pInputHash, isTabular = false, pRowIndex = null)
	{
		this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open', async () =>
		{
			// start at the upload tab
			this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Included', 'Observation');
			const tmpInput = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getInputHTMLID(pInputHTMLID));
			if (!Array.isArray(tmpInput) || tmpInput.length < 1)
			{
				console.warn(`Could not find input element for HTML ID: ${pInputHTMLID}`);
				return;
			}
			const tmpInputName = tmpInput[0] ? tmpInput[0].getAttribute('name') || 'Input Field' : 'Input Field';
			this.pict.views.HeadlightPaneObservations.setObservationPanelTarget(this.pict.views[pViewHash], tmpInput[0], tmpInputName, 'add-to-set');
			/** @type {(observationInfos: Array<{ IDObservation: number | string, Observation?: any, FileDetails?: { } }>) => Promise<void>} */
			this.pict.views.ObservationCreateMedia.InstanceConfiguration.onAfterCallback = async (observationInfos) =>
			{
				// if we have at least one observation, use the first one
				if (Array.isArray(observationInfos) && observationInfos.length > 0)
				{
					const tmpObservationInfo = observationInfos[0];
					if (tmpObservationInfo && tmpObservationInfo.IDObservation)
					{
						// update the hidden input value to the ID of the observation
						this.updateHiddenInputValue(pInputHTMLID, tmpObservationInfo.IDObservation, isTabular, pRowIndex);
						// update the display container with the new observation info
						await this.updateDisplayContainer(pInputHTMLID, tmpObservationInfo.IDObservation, tmpObservationInfo.Observation, isTabular, pRowIndex);
						// show/hide the appropriate containers
						this.hideLoadingIndicator(pInputHTMLID, isTabular, pRowIndex);
						this.hideEntryContainer(pInputHTMLID, isTabular, pRowIndex);
						this.showDisplayContainer(pInputHTMLID, isTabular, pRowIndex);
						// close/clear the panel
						this.pict.views.HeadlightPaneObservations.setObservationPanelTarget();
						this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'close');
					}
				}
			};
		});
	}

	/**
	 * This function updates the hidden input value for a given input HTML ID with the provided value.
	 * @param {*} pInputHTMLID - the HTML ID of the input element
	 * @param {*} pValue - the value to set
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	updateHiddenInputValue(pInputHTMLID, pValue, isTabular = false, pRowIndex = null)
	{
		// set the hidden input value to the ID of the observation
		const tmpHiddenElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getInputHTMLID(pInputHTMLID));
		if (Array.isArray(tmpHiddenElements) && tmpHiddenElements.length > 0)
		{
			const tmpHiddenElement = tmpHiddenElements[0];
			if (tmpHiddenElement instanceof HTMLInputElement)
			{
				this.pict.ContentAssignment.setAttribute(tmpHiddenElement, 'value', pValue);
				// trigger change event on the hidden input to notify any listeners
				const event = new Event('change', { bubbles: true });
				tmpHiddenElement.dispatchEvent(event);
			}
		}
	}

	/**
	 * This function handles the renaming of a file associated with a given input HTML ID and observation ID.
	 * @param {*} pInputHTMLID - the HTML ID of the input element
	 * @param {*} pObservationID - the ID of the observation to rename
	 * @param {*} pCurrentName - the current name of the observation
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	handleRenameFile(pInputHTMLID, pObservationID, pCurrentName, isTabular = false, pRowIndex = null)
	{
		this.fable.HeadlightModal.spawnModal({
			title: 'Rename Observation',
			modalBodyTemplate: /*HTML*/`
				<div>
					<label class="label" for="FileInputType-Modal-Rename-Field">Observation Name:</label>
					<p class="control is-expanded">
						<input class="input" type="text" id="FileInputType-Modal-Rename-Field" value="${pCurrentName}" />
					</p>
				</div>
			`,
			confirmText: 'Done',
		}, async () =>
		{
			const newName = document.getElementById('FileInputType-Modal-Rename-Field');
			if (Array.isArray(newName) || !(newName instanceof HTMLInputElement) || !newName.value || newName.value.trim().length < 1 || newName.value.trim() === pCurrentName)
			{
				console.info('No change to name, or invalid name provided');
				return;
			}
			await this.pict.providers.ObservationProvider.updateObservation({
				IDObservation: pObservationID,
				Name: newName.value.trim(),
			});
			// update the display container with the new name
			await this.updateDisplayContainer(pInputHTMLID, pObservationID, newName.value.trim(), isTabular, pRowIndex);
		});
	}

	/**
	 * This function handles the removal of a file associated with a given input HTML ID.
	 * @param {*} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 */
	handleRemoveFile(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		// show a confirmation modal
		this.fable.HeadlightModal.spawnModal({
			title: 'Remove File?',
			message: 'Are you sure you want to remove the selected file? This will not delete the underlying observation, but it will disassociate it from this form input field.',
			cancelColor: 'is-danger is-soft',
			confirmColor: 'is-danger',
			confirmText: 'Remove File',
			confirmIcon: 'trash',
		},
		() =>
		{
			// NOTE: we do NOT delete the observation here, just disassociate it from the input field (it's still available in the document's observation list)
			// NOTE: we also do NOT auto-save the document here, as the user may want to make other changes first or change their mind by closing the form...
			const tmpHiddenElements = this.pict.ContentAssignment.getElement('#' + pInputHTMLID);
			if (Array.isArray(tmpHiddenElements) && tmpHiddenElements.length > 0)
			{
				const tmpHiddenElement = tmpHiddenElements[0];
				if (tmpHiddenElement instanceof HTMLInputElement)
				{
					tmpHiddenElement.value = '';
				}
			}
			this.hideDisplayContainer(pInputHTMLID, isTabular, pRowIndex);
			this.hideLoadingIndicator(pInputHTMLID, isTabular, pRowIndex);
			this.showEntryContainer(pInputHTMLID, isTabular, pRowIndex);
			this.updateHiddenInputValue(pInputHTMLID, '', isTabular, pRowIndex);
		});
	}

	/**
	 * This function handles the file selection event for a file input element.
	 * @param {String} pInputHTMLID - the HTML ID of the input element
	 * @param {boolean} [isTabular=false] - whether this is for a tabular input
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 * @returns {Promise<boolean>} - true if successful
	 */
	async handleFileSelection(pInputHTMLID, isTabular = false, pRowIndex = null)
	{
		const tmpInputElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularFileInputHTMLID(pInputHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getFileInputHTMLID(pInputHTMLID));
		if (!Array.isArray(tmpInputElements) || tmpInputElements.length < 1)
		{
			return false;
		}
		const tmpInputElement = tmpInputElements[0];
		if (!(tmpInputElement instanceof HTMLInputElement))
		{
			return false;
		}
		const files = tmpInputElement.files;
		// if we have a file, process it
		if (files && files.length > 0)
		{
			this.hideEntryContainer(pInputHTMLID, isTabular, pRowIndex);
			this.showLoadingIndicator(pInputHTMLID, isTabular, pRowIndex);
			const tmpFile = files[0];
			// formatted object to store in the list
			const tmpFileDetails = {
				Name: tmpFile.name, // can be updated by the user - becomes the 'Observation Name'
				FileName: tmpFile.name, // for display purposes only
				File: tmpFile, // the original file object in its entirety
				ObservationType: this.pict.providers.ObservationProvider.returnObservationTypeFromFileType(tmpFile.type), // the type of observation to create
				GUID: this.pict.getUUID(), // a unique identifier for the file
			};
			await new Promise((resolve) =>
			{
				this.pict.providers.ObservationProvider.prepareObservationObjectFromFile(tmpFileDetails, this.AppData?.DocumentData?.ReportData?.IDProject,
				(observationResult) =>
				{
					const observationResultList = [];
					// add the result to the list
					observationResultList.push(observationResult);
					// update the file object with the result so we can use it for the preview replacement
					tmpFileDetails._ObservationResult = observationResult;
					// join to the document, if we have one
					this.pict.providers.ObservationProvider.joinObservationToDocumentRecord(observationResult.IDObservation, this.AppData?.DocumentData?.ReportData?.IDDocument, async () =>
					{
						// add the new observation to the document list (these are 'included' observations)
						const tmpObservations = this.AppData?.DocumentData?.ReportData?.Observations;
						// add result to our document if we have one and it is not already in the observations list
						if (tmpObservations && Array.isArray(tmpObservations) && tmpObservations.findIndex((o) => o.IDObservation === observationResult.IDObservation) < 0)
						{
							tmpObservations.push(observationResult);
						}
						resolve(observationResult);
					});
				});
			});
			// set the hidden input value to the new observation ID
			this.updateHiddenInputValue(pInputHTMLID, tmpFileDetails._ObservationResult.IDObservation, isTabular, pRowIndex);
			// if it's a document, we should auto-save the document now too
			if (this.pict.AppData?.DocumentData?.ReportData?.IDDocument)
			{
				this.pict.providers.DocumentProvider.handleSave(true);
			}
			// update the display container with the new observation details
			await this.updateDisplayContainer(pInputHTMLID, tmpFileDetails._ObservationResult.IDObservation, tmpFileDetails._ObservationResult, isTabular, pRowIndex);
			// show/hide the appropriate containers
			this.hideLoadingIndicator(pInputHTMLID, isTabular, pRowIndex);
			this.hideEntryContainer(pInputHTMLID, isTabular, pRowIndex);
			this.showDisplayContainer(pInputHTMLID, isTabular, pRowIndex);
			// clear out the file input value so we can re-use it if needed
			tmpInputElement.value = '';
			return true;
		}
		// If no file is selected, don't change anything
		return true;
	}

	/**
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be set.
	 * @param {boolean} [isTabular=false] - Whether the input is part of a tabular input set.
	 * @param {String|Number} [pRowIndex] - the row index if this is for a tabular input
	 * @returns {Promise<void>}
	 */
	async handleInputValue(pInput, pValue, isTabular = false, pRowIndex)
	{
		const tmpInputElements = isTabular ? this.pict.ContentAssignment.getElement(this.getTabularInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex)) : this.pict.ContentAssignment.getElement(this.getInputHTMLID(pInput.Macro.RawHTMLID));
		if (Array.isArray(tmpInputElements) && tmpInputElements.length > 0)
		{
			const tmpInputElement = tmpInputElements[0];
			if (tmpInputElement instanceof HTMLInputElement)
			{
				if (pValue && Number(pValue) !== 0 && !isNaN(Number(pValue)))
				{
					// set the input value to the ID
					this.pict.ContentAssignment.assignContent(tmpInputElement, pValue);
					// immediately hide the upload controls to prevent flicker during async load
					this.hideEntryContainer(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
					this.showLoadingIndicator(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
					// update the display container with the new value
					await this.updateDisplayContainer(pInput.Macro.RawHTMLID, pValue, null, isTabular, pRowIndex);
					this.hideLoadingIndicator(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
					this.showDisplayContainer(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
				}
			}
		}
		else
		{
			this.hideDisplayContainer(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
			this.hideLoadingIndicator(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
			this.showEntryContainer(pInput.Macro.RawHTMLID, isTabular, pRowIndex);
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, false);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Initializes a tabular input element.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The index of the row.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the initialization.
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true, pRowIndex);
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, false);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean}
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true, pRowIndex);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputNumericValue = this.pict.ContentAssignment.readContent(pInput.Macro.RawHTMLID);
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputNumericValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputNumericValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		const tmpInputSelectValue = this.pict.ContentAssignment.readContent(this.getTabularInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex));
		this.pict.ContentAssignment.assignContent(this.getTabularDisplayInputHTMLID(pInput.Macro.RawHTMLID, pRowIndex), tmpInputSelectValue);
		pView.dataChangedTabular(pInput.PictForm.GroupIndex, pInput.PictForm.InputIndex, pRowIndex);
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}
}

module.exports = FileSelectorInputHandler;
